import { useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { buscarEnderecoPorCep } from '../../lib/cep';
import { geocodificar } from '../../lib/geocodificacao';
import { useToast } from '../../context/ToastContext';
import MapaImovel from '../imoveis/MapaImovel';
import { ESTADOS_BR } from '../imoveis/constantes';

// Endereço da empresa, igual ao do cadastro de imóveis: CEP (preenche rua/bairro/cidade/UF pelo ViaCEP), número,
// complemento e o mapa com o pino, que acompanha o endereço (e dá para ajustar clicando no mapa).
// `valores`: estado do formulário da empresa (campos e1_*); `setCampo(nome, valor)`.
export default function EnderecoEmpresa({ valores: v, setCampo, desabilitado }) {
  const toast = useToast();
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [buscandoMapa, setBuscandoMapa] = useState(false);
  const sequenciaMapa = useRef(0);
  const enderecoInicial = useRef(null);

  const consulta = () => ({ endereco: v.e1_endereco, numero: v.e1_numero, bairro: v.e1_bairro, cidade: v.e1_cidade, estado: v.e1_estado, cep: v.e1_cep });

  async function aoSairDoCep() {
    setBuscandoCep(true);
    const achado = await buscarEnderecoPorCep(v.e1_cep);
    setBuscandoCep(false);
    if (!achado) return;
    setCampo('e1_endereco', achado.endereco || v.e1_endereco);
    setCampo('e1_bairro', achado.bairro || v.e1_bairro);
    setCampo('e1_cidade', achado.cidade || v.e1_cidade);
    setCampo('e1_estado', achado.estado || v.e1_estado);
    // o mapa acompanha sozinho: ver o efeito abaixo
  }

  async function localizar() {
    setBuscandoMapa(true);
    const achado = await geocodificar(consulta());
    setBuscandoMapa(false);
    if (achado) {
      setCampo('e1_latitude', String(achado.lat));
      setCampo('e1_longitude', String(achado.lng));
    } else {
      toast.erro('Não encontrei este endereço no mapa. Clique no mapa para marcar o ponto.');
    }
  }

  // Conforme o endereço muda (e o usuário para de digitar por 1,2 s), o pino e as coordenadas acompanham. Não roda ao
  // abrir uma empresa já salva (só em MUDANÇA); resposta de consulta antiga é descartada.
  const chaveEndereco = [v.e1_cep, v.e1_endereco, v.e1_numero, v.e1_bairro, v.e1_cidade, v.e1_estado].join('|');
  useEffect(() => {
    if (enderecoInicial.current === null) {
      enderecoInicial.current = chaveEndereco;
      return undefined;
    }
    if (desabilitado || !v.e1_cidade || !(v.e1_endereco || v.e1_cep)) return undefined;
    const minha = ++sequenciaMapa.current;
    const espera = setTimeout(async () => {
      setBuscandoMapa(true);
      const achado = await geocodificar(consulta());
      if (minha !== sequenciaMapa.current) return;
      setBuscandoMapa(false);
      if (achado) {
        setCampo('e1_latitude', String(achado.lat));
        setCampo('e1_longitude', String(achado.lng));
      }
    }, 1200);
    return () => clearTimeout(espera);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaveEndereco]);

  const temCoordenadas = Number.isFinite(parseFloat(v.e1_latitude)) && Number.isFinite(parseFloat(v.e1_longitude));
  const texto = (nome, rotulo, md, extra = {}) => (
    <Col md={md} sm={6}>
      <Form.Group controlId={nome}>
        <Form.Label>{rotulo}</Form.Label>
        <Form.Control value={v[nome] ?? ''} disabled={desabilitado} onChange={(e) => setCampo(nome, e.target.value)} autoComplete="off" {...extra} />
      </Form.Group>
    </Col>
  );

  return (
    <>
      <Card className="mb-3 mt-2">
        <Card.Header>Endereço</Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={2} sm={6}>
              <Form.Group controlId="e1_cep">
                <Form.Label>CEP {buscandoCep && <Spinner animation="border" size="sm" />}</Form.Label>
                <Form.Control value={v.e1_cep ?? ''} disabled={desabilitado} onChange={(e) => setCampo('e1_cep', e.target.value)} onBlur={aoSairDoCep} autoComplete="off" />
              </Form.Group>
            </Col>
            {texto('e1_endereco', 'Endereço', 4)}
            <Col md={2} sm={6}>
              <Form.Group controlId="e1_numero">
                <Form.Label>Número</Form.Label>
                <Form.Control inputMode="numeric" value={v.e1_numero ?? ''} disabled={desabilitado} onChange={(e) => setCampo('e1_numero', e.target.value.replace(/\D/g, ''))} autoComplete="off" />
              </Form.Group>
            </Col>
            {texto('e1_complemento', 'Complemento', 4)}
            {texto('e1_bairro', 'Bairro', 4)}
            {texto('e1_cidade', 'Cidade', 4)}
            <Col md={4} sm={6}>
              <Form.Group controlId="e1_estado">
                <Form.Label>Estado</Form.Label>
                <Form.Select value={v.e1_estado ?? ''} disabled={desabilitado} onChange={(e) => setCampo('e1_estado', e.target.value)}>
                  <option value="">Selecione</option>
                  {ESTADOS_BR.map(([sigla, nome]) => (
                    <option key={sigla} value={sigla}>{nome}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header>Mapa</Card.Header>
        <Card.Body>
          <p className="text-muted small mb-2">
            O mapa acompanha o endereço automaticamente {buscandoMapa && <Spinner animation="border" size="sm" />}. Para ajustar o ponto exato, clique no mapa.
          </p>
          <MapaImovel latitude={v.e1_latitude} longitude={v.e1_longitude} aoMudar={(lat, lng) => { setCampo('e1_latitude', lat); setCampo('e1_longitude', lng); }} />
          <Row className="g-3 mt-1 align-items-end">
            {texto('e1_latitude', 'Latitude', 3)}
            {texto('e1_longitude', 'Longitude', 3)}
            <Col md={6} className="d-flex flex-wrap gap-2">
              <Button variant="outline-primary" disabled={desabilitado || buscandoMapa} onClick={localizar}>
                {buscandoMapa ? <Spinner animation="border" size="sm" /> : 'Localizar pelo endereço'}
              </Button>
              <Button
                variant="primary"
                disabled={!temCoordenadas}
                as="a"
                target="_blank"
                rel="noreferrer"
                href={`https://www.google.com/maps/dir//${v.e1_latitude},${v.e1_longitude}/@${v.e1_latitude},${v.e1_longitude},17z?hl=pt-BR`}
              >
                Abrir no Google Maps
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </>
  );
}
